/* eslint-disable @tencent/i18next/no-literal-string */
import { ptToEmu } from '@src/slide/infrastructure/module/independent/unit';
import CPUtils from './cp-utils';
import { FONT_SCALE } from '@editor/src/page/index/components/editor/config';
import _set from 'lodash/set';
import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import { EditorDataModel } from '@src/slide/domain/ui-model/element-modeles/property/text/editor-data-model';
import { measureText } from '@tencent/alloy-editor';
import { ShapePropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/shape-property-converter';
import { DataType, ICTRegularTextRun, ICTTextBody, ICTTextParagraph, IShapeData, STShapeType } from '@tencent/slide-sdk-model';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';

/**
 * 外侧初始化时注入依赖方法
 */
export class CPHtmlInjectable {
    /**
     * @type {(() => boolean) | undefined}
     */
    public static checkCanAddIframe: () => boolean;

    /**
     * @type {(() => import(
     *      '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface.ts')
     *          .ISlideModel | undefined) | undefined}
     */
    public static getCurrentSlideModel: () => ISlideModel | undefined;

    /**
     * @type {(() => import(
     *      '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface.ts')
     *          .IPresentationModel | undefined) | undefined
     * }
     */
    public static getPresentationModel: () => IPresentationModel | undefined;
}

export function pasteTables(tableList?: any[]) {
    if (tableList?.length) {
        const currentSlide = CPHtmlInjectable.getCurrentSlideModel?.();
        tableList.forEach((table) => {
            currentSlide?.sdkData?.api?.addShapes([table]);
            CPUtils.selectLastAddElement(1);
        });
    }
}

/**
 * 根据纯文本数据生成一个形状数据
 * @param text
 * @returns
 */
export function generateShapeDataFromText(text: string): IShapeData {
    const txBodyObj: ICTTextBody = {};
    txBodyObj.p = [];
    txBodyObj.bodyPr = {};
    const paragraphItemObj: ICTTextParagraph = {};
    paragraphItemObj.egTextRun = [];
    const textRun: ICTRegularTextRun = {};
    textRun.t = text;
    textRun.rPr = {};
    paragraphItemObj.egTextRun.push({ r: textRun });
    txBodyObj.p.push(paragraphItemObj);
    const sdkDataObj: Partial<IShapeData> = {};
    sdkDataObj.nvPr = {};
    sdkDataObj.nvPr.txBox = true;
    sdkDataObj.txBody = txBodyObj;
    sdkDataObj.type = DataType.Shape;
    sdkDataObj.spPr = { prstGeom: { prst: STShapeType.rect } };
    setFrameAttributes(sdkDataObj);
    return sdkDataObj as IShapeData;
}

/**
 * 根据给定的纯文本新建一个形状
 */
export const pastePureTextAsNewShape = (text: string) => {
    const sdkDataObj = generateShapeDataFromText(text);
    CPHtmlInjectable.getCurrentSlideModel?.()?.sdkData?.api?.addShapes([sdkDataObj]);
    CPUtils.selectLastAddElement(1);
};

/**
 * 根据给定的形状数据新建一个形状
 * @param shapeData
 */
export const pasteTextDataAsNewShape = (shapeData?: IShapeData) => {
    if (!shapeData) {
        return;
    }
    const { nvPr, txBody } = shapeData;
    const sdkDataObj: Partial<IShapeData> = {};
    sdkDataObj.txBody = txBody;
    sdkDataObj.nvPr = nvPr || {};
    sdkDataObj.type = DataType.Shape;
    sdkDataObj.spPr = { prstGeom: { prst: STShapeType.rect } };
    setFrameAttributes(sdkDataObj);
    CPHtmlInjectable.getCurrentSlideModel?.()?.sdkData?.api?.addShapes([sdkDataObj as IShapeData]);
    CPUtils.selectLastAddElement(1);
};

const getTextHeight = (sdkDataObj): number => {
    const currentSlide = CPHtmlInjectable.getCurrentSlideModel();
    const { slideWidth = 0 } = CPHtmlInjectable.getPresentationModel()?.props || {};
    // sdk转ui
    const uiDataObj = { props: { style: {} }, paragraphList: [] };
    sdkDataObj = ShapePropertyConverter.appendSdkDataInheritProp(sdkDataObj, currentSlide!);

    // 将sdkDataObj的数据附加到uiDataObj
    ShapePropertyConverter.convertPartialDataFromSdkToUi(sdkDataObj, uiDataObj as unknown as IShapeModel);

    const { paragraphList } = uiDataObj;

    const width = slideWidth / 2; // 宽度为slide的一半
    let height = 500;

    // canvas 版本使用 editor 的测量逻辑
    const documentElement = EditorDataModel.convertFromTextModel({
        paragraphList,
    }, { noConvertBack: true });
    const textSize = measureText(documentElement, width);
    height = Math.round(textSize.height);
    return height / FONT_SCALE;
};

/**
 * 设置形状位置和偏移
 * @param textData
 * @returns
 */
const setFrameAttributes = (textData) => {
    if (config.supportEnterprise(EnterPriseType.ALL)) {
        textData.props.xfrm = textData.props.xfrm || {};
        textData.props.xfrm.ext = textData.props.xfrm.ext || {};
        textData.props.xfrm.ext.cx = textData.props.xfrm.ext.cx || 500 * 10000;
        textData.props.xfrm.ext.cy = textData.props.xfrm.ext.cy || 500 * 10000;
        return;
    }
    const height = getTextHeight(textData);
    const slideWidth = CPHtmlInjectable.getPresentationModel()?.props.slideWidth || 0;
    const slideHeight = CPHtmlInjectable.getPresentationModel()?.props.slideHeight || 0;
    _set(textData, 'spPr.xfrm.ext.cy', ptToEmu(height));
    _set(textData, 'spPr.xfrm.ext.cx', ptToEmu((slideWidth / FONT_SCALE) / 2));
    _set(textData, 'spPr.xfrm.off.y', ptToEmu(((slideHeight / FONT_SCALE) / 2) - (height / 2)));
    _set(textData, 'spPr.xfrm.off.x', ptToEmu((slideWidth / FONT_SCALE) / 4));
    _set(textData, 'txBody.bodyPr.spAutoFit', true);
};
